/**
 * Utility per gestire il download di file con massima compatibilità cross-device
 */
export class DownloadUtil {
  /**
   * Scarica un file da una risposta del servizio base HttpService
   * @param response - Risposta del servizio base che contiene fileUrl e fileName
   * @param fallbackFilename - Nome di fallback se fileName non è disponibile
   */
  static downloadFromHttpServiceResponse(
    response: { fileUrl: string;
      fileName?: string; },
    fallbackFilename: string
  ): void {
    // Usa fileName dalla response se disponibile, altrimenti usa fallbackFilename
    const filename = response.fileName || fallbackFilename;

    // Se fileUrl è un Blob URL, scarica direttamente il Blob
    if (response.fileUrl.startsWith("blob:")) {
      this._downloadBlobWithFallback(response.fileUrl, filename);
    } else {
      // Altrimenti usa form submit per URL normali
      this._submitDownloadForm(response.fileUrl, filename);
    }
  }

  /**
   * Scarica un Blob URL con fallback per massima compatibilità cross-device
   * @param blobUrl - Blob URL del file
   * @param filename - Nome del file
   */
  private static _downloadBlobWithFallback(blobUrl: string, filename: string): void {
    // Rileva se siamo su dispositivo mobile
    const isMobile = this._isMobileDevice();

    if (isMobile) {
      // Su mobile, usa window.open come fallback
      this._downloadBlobMobile(blobUrl);
    } else {
      // Su desktop, usa il metodo standard
      this._downloadBlobDesktop(blobUrl, filename);
    }
  }

  /**
   * Download Blob per dispositivi desktop (metodo standard)
   * @param blobUrl - Blob URL del file
   * @param filename - Nome del file
   */
  private static _downloadBlobDesktop(blobUrl: string, filename: string): void {
    try {
      // Crea link temporaneo per il download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.style.display = "none";

      // Aggiungi al DOM, clicca e rimuovi
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // Fallback: apri in nuova tab
      window.open(blobUrl, "_blank");
    } finally {
      // Pulisci il Blob URL dopo un delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    }
  }

  /**
   * Download Blob per dispositivi mobile (fallback)
   * @param blobUrl - Blob URL del file
   */
  private static _downloadBlobMobile(blobUrl: string): void {
    try {
      // Su mobile, apri in nuova tab
      const newWindow = window.open(blobUrl, "_blank");

      if (!newWindow) {
        // Se popup è bloccato, prova con location.href
        window.location.href = blobUrl;
      }
    } catch (error) {
      // Ultimo fallback: location.href
      window.location.href = blobUrl;
    } finally {
      // Pulisci il Blob URL dopo un delay più lungo per mobile
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 5000);
    }
  }

  /**
   * Rileva se il dispositivo è mobile
   * @returns true se è un dispositivo mobile
   */
  private static _isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
           || (window.innerWidth <= 768 && window.innerHeight <= 1024);
  }

  /**
   * Crea e submitta un form per il download (massima compatibilità)
   * @param fileUrl - URL del file da scaricare
   * @param filename - Nome del file
   */
  private static _submitDownloadForm(fileUrl: string, filename: string): void {
    // Crea form nascosto
    const form = document.createElement("form");
    form.method = "GET";  // Usa GET per il download
    form.action = fileUrl;
    form.target = "_blank";
    form.style.display = "none";

    // Aggiungi input per il nome file (se supportato dal backend)
    const filenameInput = document.createElement("input");
    filenameInput.type = "hidden";
    filenameInput.name = "filename";
    filenameInput.value = filename;
    form.appendChild(filenameInput);

    // Aggiungi form al DOM e submitta
    document.body.appendChild(form);
    form.submit();

    // Rimuovi form dopo submit
    setTimeout(() => {
      document.body.removeChild(form);
    }, 100);
  }
}
